import { useState } from 'react';
import type { PublicationPackage } from '../shared/campaigns';

type Destination = {
  channel: 'Instagram' | 'Facebook' | 'YouTube' | 'Outro';
  account: string;
  collaborators: string[];
};
type DestinationRow = Omit<Destination, 'collaborators'> & { collaboratorsText: string };
const channels = ['Instagram', 'Facebook', 'YouTube', 'Outro'] as const;
function initialDestinations(item?: PublicationPackage): Destination[] {
  if (item?.structuredDestinations?.length) return item.structuredDestinations;
  if (item?.destinations.length)
    return item.destinations.map((label) => {
      const channel = channels.find((name) => label.startsWith(`${name} `));
      return {
        channel: channel ?? 'Outro',
        account: channel ? label.slice(channel.length).trim() : label,
        collaborators: [],
      };
    });
  return [{ channel: 'Instagram', account: '', collaborators: [] }];
}

export function PackageDestinations({ item }: { item?: PublicationPackage }) {
  const [rows, setRows] = useState<DestinationRow[]>(() =>
    initialDestinations(item).map(({ channel, account, collaborators }) => ({
      channel,
      account,
      collaboratorsText: collaborators.join(', '),
    })),
  );
  function update(index: number, values: Partial<DestinationRow>) {
    setRows((previous) => previous.map((row, i) => (i === index ? { ...row, ...values } : row)));
  }
  return (
    <fieldset className="wide cw-destination-picker">
      <legend>Destinos e colaboradores planejados</legend>
      <p className="fine-print">
        Cada destino é uma publicação. Colaboradores participam do mesmo post e têm o aceite
        acompanhado separadamente.
      </p>
      <input
        type="hidden"
        name="structuredDestinations"
        value={JSON.stringify(
          rows.map(({ channel, account, collaboratorsText }) => ({
            channel,
            account,
            collaborators: collaboratorsText
              .split(',')
              .map((profile) => profile.trim())
              .filter(Boolean),
          })),
        )}
      />
      {rows.map((row, index) => (
        <div className="cw-destination-row" key={index}>
          <label className="field">
            <span>Canal do destino {index + 1}</span>
            <select
              value={row.channel}
              onChange={(e) => update(index, { channel: e.target.value as Destination['channel'] })}
            >
              {channels.map((channel) => (
                <option key={channel}>{channel}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Conta do destino {index + 1}</span>
            <input
              value={row.account}
              onChange={(e) => update(index, { account: e.target.value })}
              required
              maxLength={240}
              placeholder="@perfil ou nome da página"
            />
          </label>
          <label className="field wide">
            <span>Colaboradores do destino {index + 1}</span>
            <input
              value={row.collaboratorsText}
              onChange={(e) =>
                update(index, {
                  collaboratorsText: e.target.value,
                })
              }
              maxLength={2000}
              placeholder="@perfil1, @perfil2"
            />
            <small>
              Opcional. Informe os perfis separados por vírgula; isso registra o plano de
              colaboração.
            </small>
          </label>
          {rows.length > 1 && (
            <button
              type="button"
              className="secondary"
              onClick={() => setRows((previous) => previous.filter((_, i) => i !== index))}
            >
              Remover destino {index + 1}
            </button>
          )}
        </div>
      ))}
      {rows.length < 20 && (
        <button
          type="button"
          className="secondary"
          onClick={() =>
            setRows((previous) => [
              ...previous,
              { channel: 'Instagram', account: '', collaboratorsText: '' },
            ])
          }
        >
          Adicionar destino
        </button>
      )}
    </fieldset>
  );
}
